import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpsUser } from '../ownership/opsUser';
import { OrderReceiptDialog } from '../payments/OrderReceiptDialog';
import { paymentAttentionLabels, type PaymentAttentionReason } from '../scorecard/paymentAttention';
import { formatIstDateTime } from '../payments/paymentDates';
import { OrderPaymentDialogs } from '../payments/OrderPaymentDialogs';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getShipmentById } from '../../api/endpoints/admin';
import { getOrderMoneySummary } from '../../api/endpoints/payments';
import type { OrderMoneyStaffSummary } from '../../api/types';
import { SectionCard } from '../../components/ui/SectionCard';
import { OrderLabel } from '../payments/OrderLabel';
import { StatusChip } from '../payments/StatusChip';
import { formatMoney, parsePaise, paiseToDecimal } from '../payments/allocation';
import { slaFormat } from '../payments/slaFormat';
import styles from './PaymentAttentionOrders.module.css';
import { Button } from '../../components/ui/Button';
import { buttonClassName } from '../../components/ui/buttonClassName';
import { formatRoute } from '../../utils/routeLabel';

function PaymentContext({
  money,
  partnerOnly = false,
}: {
  money: OrderMoneyStaffSummary;
  partnerOnly?: boolean;
}) {
  const { t } = useTranslation();
  if (money.legacy) return <p>{t('shipments.paymentAttentionOrders.settledHistory')}</p>;
  const rows: { label: string; amount?: string; dueAt?: string | null }[] = [];
  const folded = !!money.podVerifiedAt || money.orderStatus === 'COMPLETED';
  // Requestable money excludes reservations. SLA clocks stop only on actual completion.
  if (money.advanceDueBy && !money.advanceDoneAt && !folded) {
    const paid =
      money.payments && money.allocations
        ? money.allocations.reduce((sum, line) => {
            const payment = money.payments!.find((item) => item.paymentId === line.paymentId);
            return line.status === 'ACTIVE' &&
              line.direction === 'OUT' &&
              line.purpose === 'ADVANCE' &&
              payment?.status === 'PAID'
              ? sum + parsePaise(line.amount) + parsePaise(line.tdsAmount)
              : sum;
          }, 0n)
        : null;
    const unpaid = paid == null ? null : parsePaise(money.advanceCap) - paid;
    rows.push({
      label: 'Advance unpaid',
      amount: unpaid == null ? undefined : paiseToDecimal(unpaid > 0n ? unpaid : 0n),
      dueAt: money.advanceDueBy,
    });
    rows.push({ label: 'Advance available to request', amount: money.advanceRemaining });
  }
  if (money.balanceDueBy && !money.balanceDoneAt && folded) {
    const unpaid = parsePaise(money.partnerTotal) - parsePaise(money.paidToPartner);
    rows.push({
      label: 'Balance unpaid (incl. folded advance)',
      amount: paiseToDecimal(unpaid > 0n ? unpaid : 0n),
      dueAt: money.balanceDueBy,
    });
    rows.push({ label: 'Balance available to request', amount: money.partnerRemaining });
  }
  if (!partnerOnly && !money.issuedInvoice && money.podVerifiedAt)
    rows.push({ label: 'To invoice', dueAt: money.invoiceDueBy });
  if (!partnerOnly && money.issuedInvoice && parsePaise(money.shipperOutstanding) > 0n)
    rows.push({
      label: 'Shipper outstanding',
      amount: money.shipperOutstanding,
      dueAt: money.collectionDueAt,
    });
  for (const [status, label] of [
    ['PENDING_APPROVAL', 'Requested payout (unpaid)'],
    ['APPROVED', 'Approved payout (unpaid)'],
  ] as const) {
    const pending =
      money.payments?.filter((p) => p.direction === 'OUT' && p.status === status) ?? [];
    if (pending.length)
      rows.push({
        label,
        amount: paiseToDecimal(pending.reduce((total, p) => total + parsePaise(p.amount), 0n)),
      });
  }
  if (money.payments === undefined) {
    const reserved = parsePaise(money.committedToPartner) - parsePaise(money.paidToPartner);
    if (reserved > 0n)
      rows.push({ label: 'Pending payout (status unavailable)', amount: paiseToDecimal(reserved) });
  }
  return (
    <div className={styles.context}>
      <StatusChip displayStatus={money.partnerStatus} />
      {rows.map((row) => (
        <p key={row.label}>
          <strong>{row.label}</strong>
          {row.amount != null && <> · {formatMoney(parsePaise(row.amount))}</>}
          {row.dueAt && (
            <>
              {' '}
              ·{' '}
              <span className={Date.parse(row.dueAt) < Date.now() ? styles.overdue : styles.due}>
                {slaFormat(row.dueAt)}
              </span>
            </>
          )}
        </p>
      ))}
      {money.charges.some((c) => c.status === 'PENDING_APPROVAL') && (
        <p>{t('shipments.paymentAttentionOrders.chargeAwaiting')}</p>
      )}
      {rows.length === 0 && (
        <p>
          {t('shipments.paymentAttentionOrders.noDeadline')}
        </p>
      )}
    </div>
  );
}
function AttentionOrder({ id, reason }: { id: number; reason?: PaymentAttentionReason }) {
  const { t } = useTranslation();
  const { isManager, role } = useOpsUser();
  const location = useLocation();
  const shipperOverdue = reason === 'shipperPaymentOverdue';
  const [receipt, setReceipt] = useState(false);
  const [payout, setPayout] = useState(false);
  const [paymentId, setPaymentId] = useState<number | null>(null);
  const shipment = useQuery({
    queryKey: ['admin', 'shipments', 'detail', id],
    queryFn: () => getShipmentById(id),
    staleTime: 30_000,
    retry: false,
  });
  const money = useQuery({
    queryKey: ['payments', 'shipments', id],
    queryFn: () => getOrderMoneySummary(id),
    staleTime: 30_000,
    retry: false,
  });
  const summary = money.data;
  const folded = Boolean(summary?.podVerifiedAt) || summary?.orderStatus === 'COMPLETED';
  const available = summary
    ? parsePaise(folded ? summary.partnerRemaining : summary.advanceRemaining)
    : 0n;
  const existing = summary?.payments?.find(
    (p) =>
      p.direction === 'OUT' &&
      (p.status === 'APPROVED' || p.status === 'PENDING_APPROVAL') &&
      parsePaise(p.amount) > 0n,
  );
  const partnerId = shipment.data?.assignment?.fleetManagerId;
  const eligible =
    summary &&
    !summary.legacy &&
    ['LOADED', 'IN_TRANSIT', 'AT_DROP', 'DELIVERED', 'COMPLETED'].includes(summary.orderStatus) &&
    summary.payeeInfo?.payeeEligible &&
    !summary.charges.some((c) => c.status === 'PENDING_APPROVAL') &&
    partnerId != null;
  const route =
    money.data?.route ??
    (shipment.data ? formatRoute(shipment.data) : undefined);
  const shipper = shipment.data?.shipperName;
  const shipperId = shipment.data?.shipperId;
  const receiptEligible =
    (isManager || role === 'OPS_EXECUTIVE') &&
    summary &&
    !summary.legacy &&
    ['LOADED', 'IN_TRANSIT', 'AT_DROP', 'DELIVERED', 'COMPLETED'].includes(summary.orderStatus) &&
    summary.issuedInvoice != null &&
    parsePaise(summary.shipperOutstanding) > 0n &&
    shipperId != null &&
    !shipment.isError;
  const partnerPrimary = !shipperOverdue && reason !== 'invoiceOverdue';
  const partner = money.data?.payeeInfo?.companyName ?? shipment.data?.assignment?.fleetManagerName;
  return (
    <article className={styles.row} aria-label={`Order #${id}`}>
      <div className={styles.identity}>
        <OrderLabel
          shipmentId={id}
          route={route}
          vehicleNumber={shipment.data?.assignment?.vehicleRegistrationNumber}
        />
        {shipment.isLoading && <p role="status">{t('shipments.paymentAttentionOrders.loadingOrder')}</p>}
        {shipment.isError && (
          <p role="alert">
            {t('shipments.paymentAttentionOrders.orderUnavailable')}{' '}
            <Button variant="secondary" onClick={() => void shipment.refetch()}>
              {t('shipments.paymentAttentionOrders.retryOrder', { id })}
            </Button>
          </p>
        )}
        {!shipment.isLoading && (
          <p>
            {shipperOverdue
              ? `Shipper: ${shipper || 'Not available'}`
              : `Partner: ${partner || 'Not available'}`}
          </p>
        )}
      </div>
      <div>
        {money.isLoading ? (
          <p role="status">{t('shipments.paymentAttentionOrders.loadingPayment')}</p>
        ) : money.isError ? (
          <p role="alert">
            {t('shipments.paymentAttentionOrders.paymentUnavailable')}{' '}
            <Button variant="secondary" onClick={() => void money.refetch()}>
              {t('shipments.paymentAttentionOrders.retryPayments', { id })}
            </Button>
          </p>
        ) : (
          money.data &&
          (shipperOverdue ? (
            <div className={styles.context}>
              {summary?.legacy ? (
                <p>{t('shipments.paymentAttentionOrders.settledHistory')}</p>
              ) : (
                <>
                  <StatusChip displayStatus={money.data.shipperStatus} />
                  <p>
                    <strong>{t('shipments.paymentAttentionOrders.invoice')}</strong> ·{' '}
                    {money.data.issuedInvoice?.invoiceNumber ?? 'No issued invoice'}
                  </p>
                  <p>
                    <strong>{t('shipments.paymentAttentionOrders.shipperOutstanding')}</strong> ·{' '}
                    {formatMoney(parsePaise(money.data.shipperOutstanding))}
                  </p>
                  <p>
                    <strong>{t('shipments.paymentAttentionOrders.paymentDue')}</strong> ·{' '}
                    {money.data.collectionDueAt ? (
                      <>
                        {formatIstDateTime(money.data.collectionDueAt)}
                        {t('shipments.paymentAttentionOrders.istSuffix')}{' '}
                        <span
                          className={
                            Date.parse(money.data.collectionDueAt) < Date.now()
                              ? styles.overdue
                              : styles.due
                          }
                        >
                          {slaFormat(money.data.collectionDueAt)}
                        </span>
                      </>
                    ) : (
                      (money.data.issuedInvoice?.dueDate ?? 'Not available')
                    )}
                  </p>
                </>
              )}
            </div>
          ) : (
            <PaymentContext money={money.data} />
          ))
        )}
      </div>
      {shipperOverdue && receiptEligible && (
        <Button variant="primary" className={styles.action} onClick={() => setReceipt(true)}>
          {t('shipments.paymentAttentionOrders.recordReceipt')}
        </Button>
      )}
      {shipperOverdue && summary && !summary.legacy && !receiptEligible && (
        <p>{t('shipments.paymentAttentionOrders.noReceiptAction')}</p>
      )}
      {shipperOverdue && (
        <details className={styles.partnerContext}>
          <summary>{t('shipments.paymentAttentionOrders.partnerContext')}</summary>
          <p>
            {t('shipments.paymentAttentionOrders.partnerLabel')}
            {partner || 'Not available'}
          </p>
          {summary && <PaymentContext money={summary} partnerOnly />}
        </details>
      )}
      {existing && (
        <Button
          variant={partnerPrimary ? 'primary' : 'secondary'}
          className={styles.action}
          onClick={() => setPaymentId(existing.paymentId)}
        >
          {existing.status === 'APPROVED' ? 'View approved payout' : 'View payout request'}
        </Button>
      )}
      {!existing && eligible && available > 0n && (
        <Button
          variant={partnerPrimary ? 'primary' : 'secondary'}
          className={styles.action}
          onClick={() => setPayout(true)}
        >
          {t('shipments.paymentAttentionOrders.requestPayout', { amount: formatMoney(available) })}
        </Button>
      )}
      {(paymentId || payout) && (
        <OrderPaymentDialogs
          shipmentId={id}
          partnerId={partnerId}
          advanceTerms={
            summary
              ? {
                  shipmentId: id,
                  partnerFreight: summary.partnerFreight,
                  advanceCap: summary.advanceCap,
                }
              : undefined
          }
          payeeName={partner ?? undefined}
          isManager={isManager}
          readOnly={summary?.legacy}
          payout={payout}
          paymentId={paymentId}
          onClosePayout={() => setPayout(false)}
          onClosePayment={() => setPaymentId(null)}
        />
      )}
      {receipt && receiptEligible && shipperId != null && (
        <OrderReceiptDialog
          shipmentId={id}
          shipperId={shipperId}
          shipperName={shipper ?? `Shipper #${shipperId}`}
          onClose={() => setReceipt(false)}
        />
      )}
      <Link
        state={{
          from: location.pathname + location.search,
          fromLabel: reason ? paymentAttentionLabels[reason] : 'Payment attention',
        }}
        className={buttonClassName('secondary')}
        to={`/shipments/${id}?tab=payments`}
        aria-label={`Open payments for order #${id}`}
      >
        {t('shipments.paymentAttentionOrders.openPayments')}
        <span aria-hidden="true">→</span>
      </Link>
    </article>
  );
}
/** At most 50 exact scorecard IDs, two cached staff reads each; no list scan or profile reads. */
export function PaymentAttentionOrders({
  ids,
  reason,
}: {
  ids: number[];
  reason?: PaymentAttentionReason;
}) {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={reason ? paymentAttentionLabels[reason] : 'Orders needing payment attention'}
      action={
        <Link className={buttonClassName('secondary')} to="/shipments">
          {t('shipments.paymentAttentionOrders.allOrders')}
        </Link>
      }
    >
      <div className={styles.list}>
        <p>
          {ids.length} {ids.length === 1 ? 'order' : 'orders'}
          {t('shipments.paymentAttentionOrders.listSummary')}
        </p>
        {ids.length === 0 && <p>{t('shipments.paymentAttentionOrders.noIds')}</p>}
        {ids.map((id) => (
          <AttentionOrder key={`${reason ?? 'general'}:${id}`} id={id} reason={reason} />
        ))}
      </div>
    </SectionCard>
  );
}
